import React from "react";
import { LuScrollText, LuPackage, LuSparkles, LuWrench, LuCircleArrowUp, LuStickyNote } from "react-icons/lu";

// ── Section config ────────────────────────────────────────────────────────────

const SECTIONS = [
  {
    key: "added",
    label: "Added",
    icon: LuSparkles,
    colorClasses: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dotClass: "bg-emerald-500",
  },
  {
    key: "improved",
    label: "Improved",
    icon: LuCircleArrowUp,
    colorClasses: "bg-blue-50 text-blue-700 border-blue-200",
    dotClass: "bg-blue-500",
  },
  {
    key: "fixed",
    label: "Fixed",
    icon: LuWrench,
    colorClasses: "bg-amber-50 text-amber-700 border-amber-200",
    dotClass: "bg-amber-500",
  },
  {
    key: "notes",
    label: "Notes",
    icon: LuStickyNote,
    colorClasses: "bg-slate-50 text-slate-600 border-slate-200",
    dotClass: "bg-slate-400",
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionTag({ label, icon: Icon, colorClasses }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${colorClasses}`}
    >
      <Icon size={11} />
      {label}
    </span>
  );
}

/**
 * Each item in an `added / improved / fixed / notes` array can be either:
 *   - a plain string  →  rendered as a single bullet
 *   - { section, items }  →  rendered as a labeled sub-group with its own bullets
 */
function ChangeList({ items, dotClass }) {
  return (
    <div className="mt-2 space-y-4">
      {items.map((item, i) => {
        // ── Grouped sub-section ──────────────────────────────────────────────
        if (item && typeof item === "object" && item.section) {
          return (
            <div key={i}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
                {item.section}
              </p>
              <ul className="space-y-2">
                {(item.items || []).map((sub, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <span
                      className={`mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full ${dotClass}`}
                    />
                    <span className="text-sm leading-relaxed text-slate-600">{sub}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        // ── Plain string bullet ──────────────────────────────────────────────
        return (
          <ul key={i} className="space-y-2">
            <li className="flex items-start gap-3">
              <span
                className={`mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full ${dotClass}`}
              />
              <span className="text-sm leading-relaxed text-slate-600">{item}</span>
            </li>
          </ul>
        );
      })}
    </div>
  );
}

function EmptyEntry({ version }) {
  return (
    <p className="text-sm italic text-slate-400">
      Release notes for {version} are being prepared. Check back soon.
    </p>
  );
}

function VersionEntry({ entry, isLatest }) {
  const hasContent = SECTIONS.some((s) => entry[s.key]?.length > 0);

  return (
    <div className="relative flex gap-6">
      {/* Timeline rail */}
      <div className="flex flex-col items-center">
        <div
          className={`relative z-10 mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border-2 shadow-sm ${
            isLatest
              ? "border-blue-400 bg-blue-50"
              : "border-slate-200 bg-white"
          }`}
        >
          <LuPackage
            size={16}
            className={isLatest ? "text-blue-500" : "text-slate-400"}
          />
        </div>
        <div className="mt-2 w-px flex-1 bg-slate-200" />
      </div>

      {/* Content */}
      <div className="mb-12 min-w-0 flex-1 pb-2">
        {/* Version + date row */}
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span
            className={`rounded-lg border px-3 py-1 font-mono text-sm font-bold tracking-tight ${
              isLatest
                ? "border-blue-200 bg-blue-50 text-blue-700"
                : "border-slate-200 bg-slate-50 text-slate-600"
            }`}
          >
            {entry.version}
          </span>
          {isLatest ? (
            <span className="rounded-full bg-blue-500 px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-white">
              Latest
            </span>
          ) : null}
          {entry.date ? (
            <span className="text-sm text-slate-400">{entry.date}</span>
          ) : null}
        </div>

        {/* Optional release title */}
        {entry.title ? (
          <h3 className="mb-4 text-lg font-semibold text-slate-800">
            {entry.title}
          </h3>
        ) : null}

        {/* Sections */}
        {hasContent ? (
          <div className="space-y-5">
            {SECTIONS.map((section) => {
              const items = entry[section.key] || [];
              if (items.length === 0) return null;
              return (
                <div key={section.key}>
                  <SectionTag
                    label={section.label}
                    icon={section.icon}
                    colorClasses={section.colorClasses}
                  />
                  <ChangeList items={items} dotClass={section.dotClass} />
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyEntry version={entry.version} />
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * ChangelogPage
 *
 * Props:
 *   changelog  object  One of the named exports from changelogData.js
 *                      Shape: { productName: string, entries: Entry[] }
 */
const ChangelogPage = ({ changelog }) => {
  const { productName, entries = [] } = changelog;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">

        {/* Page header */}
        <div className="mb-10 rounded-2xl border border-slate-200 bg-white px-8 py-7 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 rounded-xl bg-blue-100 p-3">
              <LuScrollText size={24} className="text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
                {productName}
                <span className="ml-2 font-normal text-slate-400">Changelog</span>
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Release history and product updates, newest first.
              </p>
            </div>
          </div>
        </div>

        {/* Timeline */}
        {entries.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-8 py-12 text-center">
            <LuScrollText size={32} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-400">No changelog entries yet.</p>
          </div>
        ) : (
          <div>
            {entries.map((entry, i) => (
              <VersionEntry key={entry.version} entry={entry} isLatest={i === 0} />
            ))}

            {/* End cap */}
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <div className="h-px flex-1 bg-slate-200" />
              <span>Beginning of changelog</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangelogPage;
